/**
 * ============================================================================
 * useDynamicPricing Hook - TanStack Query Integration
 * ============================================================================
 *
 * Manages dynamic pricing rules for venues with full CRUD operations
 * Supports time-based, day-based, and date-range pricing rules
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export type RuleType = 'time_based' | 'day_based' | 'date_range';
export type AdjustmentType = 'fixed_amount' | 'percentage';

export interface DynamicPricingRule {
  id: number;
  venue_id: number;
  court_id?: number | null;
  sport_id?: number | null;
  name: string;
  description?: string;
  rule_type: RuleType;
  adjustment_type: AdjustmentType;
  adjustment_value: number;
  applies_from_time?: string | null;
  applies_to_time?: string | null;
  applies_on_days?: number[] | string | null;
  applies_from_date?: string | null;
  applies_to_date?: string | null;
  priority: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  // Relationships
  court?: { id: number; name: string };
  sport?: { id: number; name: string };
}

export interface CreatePricingRuleData {
  venue_id: number;
  court_id?: number | null;
  sport_id?: number | null;
  name: string;
  description?: string;
  rule_type: RuleType;
  adjustment_type: AdjustmentType;
  adjustment_value: number;
  applies_from_time?: string | null;
  applies_to_time?: string | null;
  applies_on_days?: number[] | null;
  applies_from_date?: string | null;
  applies_to_date?: string | null;
  priority?: number;
  is_active?: boolean;
}

/**
 * Fetch all pricing rules for the authenticated vendor
 */
async function fetchPricingRules(token: string): Promise<DynamicPricingRule[]> {
  const response = await fetch(`${API_BASE_URL}/v1/pricing-rules`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch pricing rules');
  }

  const data = await response.json();
  return data.rules || [];
}

/**
 * Create a new pricing rule
 */
async function createPricingRule(
  data: CreatePricingRuleData,
  token: string
): Promise<DynamicPricingRule> {
  const response = await fetch(`${API_BASE_URL}/v1/pricing-rules`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to create pricing rule');
  }

  const result = await response.json();
  return result.rule;
}

/**
 * Toggle pricing rule active status
 */
async function togglePricingRule(
  ruleId: number,
  currentStatus: boolean,
  token: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/v1/pricing-rules/${ruleId}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ is_active: !currentStatus }),
  });

  if (!response.ok) {
    throw new Error('Failed to toggle pricing rule');
  }
}

/**
 * Delete a pricing rule
 */
async function deletePricingRule(ruleId: number, token: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/v1/pricing-rules/${ruleId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to delete pricing rule');
  }
}

/**
 * Hook to fetch pricing rules
 */
export function usePricingRules(venueId?: number) {
  const token = sessionStorage.getItem('vendor_token') || '';

  return useQuery({
    queryKey: ['pricing-rules', venueId],
    queryFn: () => fetchPricingRules(token),
    enabled: !!token && !!venueId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to create a pricing rule
 */
export function useCreatePricingRule() {
  const queryClient = useQueryClient();
  const token = sessionStorage.getItem('vendor_token') || '';

  return useMutation({
    mutationFn: (data: CreatePricingRuleData) => createPricingRule(data, token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules', variables.venue_id] });
    },
  });
}

/**
 * Hook to toggle pricing rule active status
 */
export function useTogglePricingRule() {
  const queryClient = useQueryClient();
  const token = sessionStorage.getItem('vendor_token') || '';

  return useMutation({
    mutationFn: ({ ruleId, currentStatus }: { ruleId: number; currentStatus: boolean }) =>
      togglePricingRule(ruleId, currentStatus, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
    },
  });
}

/**
 * Hook to delete a pricing rule
 */
export function useDeletePricingRule() {
  const queryClient = useQueryClient();
  const token = sessionStorage.getItem('vendor_token') || '';

  return useMutation({
    mutationFn: (ruleId: number) => deletePricingRule(ruleId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
    },
  });
}
